import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { AdminAuthService } from '../services/admin-auth.service';
import { LanguageService } from '../services/language.service';
import { SwalService } from '../services/swal.service';
import {
  SKIP_API_TOAST,
  parseApiEnvelope,
  handleApiEnvelope,
  isFetchRequest,
  isAuthFailureEnvelope,
  ApiEnvelope
} from '../utils/api-response';
import { readRecommendedAddOn, readRecommendedPlan, RecommendedAddOn } from '../utils/recommended-plan';
import { RecommendedPlan } from '../models/models';

const AUTH_SKIP_URL =
  /\/(login_and_registration_otp|verify_otp|login_with_password|forgot_password|get_country_code_list|get_language_list|Contact\/submit_inquiry)\b/i;

const ADMIN_API_URL = /\/Admin\//i;
/** Public marketing APIs — no user token, never bounce to /login. */
const PUBLIC_API_URL = /\/(Blog|Website)\//i;

@Injectable()
export class ApiResponseInterceptor implements HttpInterceptor {

  constructor(
    private swal: SwalService,
    private router: Router,
    private auth: AuthService,
    private adminAuth: AdminAuthService,
    private lang: LanguageService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const skipToast = req.context.get(SKIP_API_TOAST);
    const silent = skipToast || isFetchRequest(req.url);
    const showSuccessToast = req.method !== 'GET' && !silent;

    const hadAuthenticatedSession = this.auth.hasValidSessionForApi();
    const isAdminApi = ADMIN_API_URL.test(req.url);
    const isPublicApi = this.isPublicApi(req.url);
    const onAdminRoute = this.isAdminRoute();
    /** Admin screens and Admin APIs must never bounce to the customer /login. */
    const skipCustomerAuthRedirect = isPublicApi || isAdminApi || onAdminRoute;

    if (this.requiresAuth(req.url) && !hadAuthenticatedSession && !isAdminApi && !isPublicApi) {
      if (!onAdminRoute && !this.isCustomerLoginRoute()) {
        void this.router.navigate(['/login']);
      }
      return throwError(() => new Error('No active login session'));
    }

    const envelopeDeps = {
      swal: this.swal,
      router: this.router,
      showSuccessToast,
      silent,
      hadAuthenticatedSession,
      skipAuthRedirect: skipCustomerAuthRedirect,
      invalidateSession: () => this.auth.invalidateSession()
    };

    return next.handle(req).pipe(
      tap(event => {
        if (!(event instanceof HttpResponse)) return;
        const env = parseApiEnvelope(event.body);
        if (!env) return;
        if (
          isAdminApi &&
          !this.isAdminLoginRequest(req.url) &&
          isAuthFailureEnvelope(env)
        ) {
          this.handleAdminUnauthorized();
          return;
        }
        if (!isAdminApi && this.offerLimitUpgrade(env, req.url)) return;
        handleApiEnvelope(env, envelopeDeps);
      }),

      catchError((err: { status?: number; error?: unknown; message?: string }) => {
        const env = parseApiEnvelope(err?.error);
        if (env) {
          if (
            isAdminApi &&
            !this.isAdminLoginRequest(req.url) &&
            (err.status === 401 || isAuthFailureEnvelope(env))
          ) {
            this.handleAdminUnauthorized();
          } else if (isAdminApi || !this.offerLimitUpgrade(env, req.url)) {
            handleApiEnvelope(env, envelopeDeps);
          }
        } else if (!silent) {
          this.handleHttpError(err, req.url);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * AI Entry and Add Sub User limits open the matching add-on.
   * Other limit-aware creates return `showRecommend: 1` + `recommendedPlan`
   * and Buy Now opens the Plan Pay & Confirm flow.
   * The API message is shown first in either case.
   */
  private offerLimitUpgrade(env: ApiEnvelope, url: string): boolean {
    const addOn = readRecommendedAddOn(env.data, url, env.responseMessage, env.responseStatus);
    if (addOn) {
      void this.promptRecommendedAddOn(env.responseMessage || '', addOn);
      return true;
    }
    const plan = readRecommendedPlan(env.data);
    if (!plan) return false;
    void this.promptRecommendedPlan(env.responseMessage || '', plan);
    return true;
  }

  private async promptRecommendedAddOn(message: string, addOn: RecommendedAddOn): Promise<void> {
    const buy = await this.swal.confirmRecommendPlan(
      this.lang.t('addOnRecommendTitle'),
      message || this.lang.t('addOnRecommendFallback'),
      this.lang.t('planBuyNow'),
      this.lang.t('planMaybeLater')
    );
    if (!buy) return;
    void this.router.navigate(['/add-ons'], {
      queryParams: {
        recommendAddOn: addOn.kind || null,
        recommendAddOnId: addOn.addOnId > 0 ? addOn.addOnId : null
      }
    });
  }

  private async promptRecommendedPlan(message: string, plan: RecommendedPlan): Promise<void> {
    const buy = await this.swal.confirmRecommendPlan(
      this.lang.t('planRecommendTitle'),
      message || this.lang.t('planRecommendFallback'),
      this.lang.t('planBuyNow'),
      this.lang.t('planMaybeLater')
    );
    if (!buy) return;
    void this.router.navigate(['/plan'], {
      queryParams: {
        recommendPlanId: plan.planId,
        billingCycle: plan.billingCycle || null
      }
    });
  }

  private handleAdminUnauthorized(): void {
    this.adminAuth.logout();
    if (!this.router.url.includes('/admin/login')) {
      void this.router.navigate(['/admin/login']);
    }
  }

  /** Customer login only. `/admin/login` must not match. */
  private isCustomerLoginRoute(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return path === '/login' || path.startsWith('/login/');
  }

  private isAdminRoute(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return path === '/admin' || path.startsWith('/admin/');
  }

  private isAdminLoginRequest(url: string): boolean {
    return /\/Admin\/login\b/i.test(url);
  }

  private isPublicApi(url: string): boolean {
    return PUBLIC_API_URL.test(url) || AUTH_SKIP_URL.test(url);
  }

  private requiresAuth(url: string): boolean {
    if (ADMIN_API_URL.test(url) || this.isPublicApi(url)) return false;
    return true;
  }

  private handleHttpError(err: {
    status?: number;
    error?: unknown;
    message?: string;
  }, url = ''): void {
    const body = err?.error as { responseMessage?: string; message?: string } | undefined;
    const status = err?.status;
    const msg =
      body?.responseMessage ||
      body?.message ||
      err?.message ||
      'Network error';

    if (status === 401 && ADMIN_API_URL.test(url)) {
      if (!this.isAdminLoginRequest(url)) {
        this.handleAdminUnauthorized();
      }
      return;
    }

    const env = parseApiEnvelope(err?.error);
    const hadSession = this.auth.hasValidSessionForApi();
    const skipAuthRedirect =
      this.isPublicApi(url) || ADMIN_API_URL.test(url) || this.isAdminRoute();

    if (this.isPublicApi(url)) {
      return;
    }

    if (env && isAuthFailureEnvelope(env)) {
      if (skipAuthRedirect) return;
      if (hadSession) {
        this.swal.tokenError(env.responseMessage || 'Please login again.');
        setTimeout(() => this.auth.invalidateSession(), 1800);
      } else if (!this.isCustomerLoginRoute()) {
        this.auth.invalidateSession();
      }
      return;
    }

    if (status === 401 && skipAuthRedirect) {
      return;
    }

    if (status === 401 && hadSession) {
      this.swal.tokenError(msg || 'Session expired. Please login again.');
      setTimeout(() => this.auth.invalidateSession(), 1800);
    } else if (status === 401 && !this.isCustomerLoginRoute()) {
      this.auth.invalidateSession();
    } else if (status === 500) {
      this.swal.serverError(msg);
    } else if (status === 0) {
      this.swal.error('Server error or network issue. Please check your connection and try again.');
    } else if (status) {
      this.swal.error(msg);
    }
  }
}
